import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Building2, Briefcase, Contact as ContactIcon, Download, Globe, Mail, MapPin, MessageCircle, Pencil, Phone, Plus, Smartphone, Star, Tag, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Btn, Empty, InfoRows, Modal, Page, Spinner, TopBar, useIsDesktop } from "@/components/kit";
import ContactDetailDesktop from "@/pages/ContactDetailDesktop";
import WhatsAppSheet, { waNumber } from "@/components/WhatsAppSheet";
import EventPicker from "@/components/EventPicker";
import ReminderSection from "@/components/ReminderSection";
import { useAddToEvent, useCategories, useContact, useContactEvents, useDeleteContact, useFileToken, useRemoveFromEvent, useUpdateContact } from "@/hooks/queries";
import { buildVcf, googleContactsUrl, joinAddress, download, fmtDate, imageUri, vcfName } from "@/lib/toto";

const Action = ({ icon: Icon, label, href, testid, onOpen }) => (
  <a href={href || undefined} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" onClick={(e) => (!href ? (e.preventDefault(), toast(`No ${label.toLowerCase()} available.`)) : onOpen && (e.preventDefault(), onOpen()))}
    className={`flex flex-col items-center justify-center gap-1 h-16 rounded-[14px] bg-[#E5E5FD] text-[#0000EC] transition-[transform,opacity] active:scale-95 ${href ? "hover:bg-[#d8d8fc]" : "opacity-50"}`} data-testid={testid}>
    <Icon className="w-5 h-5" strokeWidth={2.2} /><span className="text-[13px] font-bold">{label}</span>
  </a>
);

export default function ContactDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: c, isLoading, isError } = useContact(id);
  const cats = useCategories().data ?? [];
  const fileToken = useFileToken();
  const update = useUpdateContact(id);
  const del = useDeleteContact();
  const events = useContactEvents(id).data ?? [];
  const add = useAddToEvent();
  const remove = useRemoveFromEvent();
  const [confirmDel, setConfirmDel] = useState(false);
  const [picker, setPicker] = useState(false);
  const [preview, setPreview] = useState(null);
  const isDesktop = useIsDesktop();
  const [waOpen, setWaOpen] = useState(false);

  if (isLoading) return <Spinner />;
  if (isError || !c) return <Page><TopBar title="Contact" /><Empty icon={ContactIcon} title="Contact not found" action={<Link to="/contacts" className="t-btn t-btn-primary">Back to Contacts</Link>} /></Page>;

  const catName = cats.find((x) => x.id === c.categoryId)?.name ?? "Uncategorized";
  const digits = (p) => (p || "").replace(/[^\d+]/g, "");
  const wa = waNumber(c.whatsapp || c.phone || c.alternatePhone);
  const site = c.website ? (/^https?:\/\//i.test(c.website) ? c.website : `https://${c.website}`) : null;
  const front = imageUri(c.cardImageUrl, fileToken);
  const back = imageUri(c.cardBackImageUrl, fileToken);
  const ids = new Set(events.map((e) => e.id));

  const saveVcf = () => {
    download(buildVcf(c, catName), vcfName(c), "text/vcard;charset=utf-8");
    update.mutate({ deviceContactSaved: true });
    toast.success("vCard downloaded ✓ Open it to add to your address book.");
  };
  const google = () => {
    window.open(googleContactsUrl(c), "_blank", "noopener");
    toast("Opening Google Contacts with the details filled in — review and click Save.", { duration: 6000 });
  };
  const onDelete = async () => {
    try { await del.mutateAsync(id); toast.success("Contact deleted."); nav("/contacts"); } catch (e) { toast.error(e.message); }
  };
  const pick = async (ev) => {
    setPicker(false);
    if (!ev || ids.has(ev.id)) return;
    try { await add.mutateAsync({ eventId: ev.id, contactId: id }); toast.success(`Added to ${ev.name}`); } catch (e) { toast.error(e.message); }
  };

  return (
    <Page>
      {isDesktop ? (
        <ContactDetailDesktop c={c} id={id} catName={catName} front={front} back={back} events={events} wa={wa} site={site} tel={c.phone && `tel:${digits(c.phone)}`}
          onFav={() => update.mutate({ isFavorite: !c.isFavorite })} onDelete={() => setConfirmDel(true)} saveVcf={saveVcf} google={google}
          setPreview={setPreview} openPicker={() => setPicker(true)} removeEvent={(eventId) => remove.mutate({ eventId, contactId: id })} onWhatsApp={() => setWaOpen(true)} />
      ) : (<>
      <TopBar title="Contact" align="left" right={<>
        <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F1F5F9]" onClick={() => update.mutate({ isFavorite: !c.isFavorite })} data-testid="contact-favorite"><Star className={`w-[22px] h-[22px] ${c.isFavorite ? "fill-[#D97706] text-[#D97706]" : "text-[#475569]"}`} /></button>
        <Link to={`/contacts/${id}/edit`} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F1F5F9]" data-testid="contact-edit"><Pencil className="w-[21px] h-[21px]" /></Link>
        <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#FEF2F2]" onClick={() => setConfirmDel(true)} data-testid="contact-delete"><Trash2 className="w-[22px] h-[22px] text-[#DC2626]" /></button>
      </>} />
      <div className="lg:grid lg:grid-cols-[1fr_1fr] lg:gap-10">
        <div>
          <div className="flex flex-col items-center text-center mt-2">
            <span className="w-20 h-20 rounded-full bg-[#0000EC] text-white text-[32px] font-bold flex items-center justify-center">{(c.name || c.companyName || "?")[0].toUpperCase()}</span>
            <h1 className="mt-3 text-[24px] font-extrabold" data-testid="contact-name">{c.name || c.companyName}</h1>
            {c.designation && <p className="text-[16px] font-bold text-[#475569]">{c.designation}</p>}
            {c.companyName && <p className="text-[15px] text-[#64748B]">{c.companyName}</p>}
          </div>
          <div className="grid grid-cols-4 gap-2 mt-6">
            <Action icon={Phone} label="Call" href={c.phone && `tel:${digits(c.phone)}`} testid="action-call" />
            <Action icon={MessageCircle} label="WhatsApp" href={wa && `https://wa.me/${wa}`} onOpen={() => setWaOpen(true)} testid="action-whatsapp" />
            <Action icon={Mail} label="Email" href={c.email && `mailto:${c.email.split(/[,;\s]+/).filter(Boolean).join(",")}`} testid="action-email" />
            <Action icon={Globe} label="Website" href={site} testid="action-website" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5">
            <Btn onClick={saveVcf} testid="save-vcard"><Download className="w-5 h-5" />Download vCard</Btn>
            <Btn variant="outline" onClick={google} testid="save-google-contacts"><Smartphone className="w-5 h-5" />Add to Google Contacts</Btn>
          </div>
          <h2 className="t-section mt-8 mb-3">Business Information</h2>
          <InfoRows testid="contact-business-info" rows={[
            { icon: Building2, label: "Company", value: c.companyName },
            { icon: Briefcase, label: "Designation", value: c.designation },
            { icon: Tag, label: "Category", value: catName },
            { icon: Phone, label: "Mobile", value: c.phone },
            { icon: Phone, label: "Alternate", value: c.alternatePhone },
            { icon: Mail, label: "Email", value: c.email },
          ]} />
          <div className="flex items-center justify-between mt-8 mb-2">
            <h2 className="t-section">Events</h2>
            <button className="flex items-center gap-1 t-link" onClick={() => setPicker(true)} data-testid="contact-add-event"><Plus className="w-4 h-4" />Add to Event</button>
          </div>
          {events.length ? (
            <div className="flex flex-wrap gap-2">
              {events.map((e) => (
                <span key={e.id} className="inline-flex items-center gap-1.5 pl-3 pr-1 h-9 rounded-full bg-[#E5E5FD] text-[#0000EC] text-[14px] font-semibold" data-testid={`contact-event-${e.id}`}>
                  <Link to={`/events/${e.id}`}>{e.name} · {fmtDate(e.eventDate)}</Link>
                  <button className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/60" onClick={() => remove.mutate({ eventId: e.id, contactId: id })} data-testid={`contact-event-remove-${e.id}`}><X className="w-4 h-4" /></button>
                </span>
              ))}
            </div>
          ) : <p className="text-[15px] text-[#64748B]">Not linked to any event yet.</p>}
          <ReminderSection contactId={id} />
        </div>
        <div>
          <h2 className="t-section mt-8 mb-3">Address</h2>
          <InfoRows testid="contact-address" rows={[
            { icon: MapPin, label: "Address", value: joinAddress(c) },
            { icon: MapPin, label: "City", value: c.city },
            { icon: MapPin, label: "State", value: c.state },
            { icon: MapPin, label: "Pincode", value: c.postalCode },
            { icon: MapPin, label: "Country", value: c.country },
          ]} />
          {!c.address && !c.city && !c.state && <p className="text-[15px] text-[#64748B]">No address saved.</p>}
          {(front || back) && (
            <>
              <h2 className="t-section mt-8 mb-1">Visiting Card</h2>
              <p className="text-[13px] text-[#64748B] mb-3">Click to view full card</p>
              <div className="grid gap-3">
                {[front, back].filter(Boolean).map((src, i) => (
                  <button key={src} onClick={() => setPreview(src)} className="rounded-[16px] overflow-hidden border border-[#E2E8F0]" data-testid={i ? "contact-card-back-image" : "contact-card-image"}>
                    <img src={src} alt="Visiting card" className="w-full aspect-[1.7/1] object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
          {c.notes && (<><h2 className="t-section mt-8 mb-3">Notes</h2><p className="t-card p-4 text-[15px] whitespace-pre-wrap">{c.notes}</p></>)}
        </div>
      </div>
      </>)}
      <Modal open={confirmDel} onClose={() => setConfirmDel(false)} title="Delete contact?" description="This contact and its card images will be permanently removed." testid="delete-contact-modal">
        <div className="grid grid-cols-2 gap-2"><Btn variant="ghost" onClick={() => setConfirmDel(false)} testid="delete-contact-cancel">Cancel</Btn><Btn variant="danger" loading={del.isPending} onClick={onDelete} testid="delete-contact-confirm">Delete</Btn></div>
      </Modal>
      <Modal open={!!preview} onClose={() => setPreview(null)} title="Visiting Card" wide testid="card-preview">
        {preview && <img src={preview} alt="Visiting card" className="w-full rounded-xl" data-testid="card-preview-image" />}
      </Modal>
      <WhatsAppSheet open={waOpen} onClose={() => setWaOpen(false)} contact={c} />
      <EventPicker open={picker} onClose={() => setPicker(false)} selectedIds={ids} onPick={pick} testid="contact-event-picker" />
    </Page>
  );
}
